import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  courseById,
  hasFullLifecycle,
  derivePaymentStatus,
  STUDENT_STATUSES,
  IDPH_STATUSES,
  EXAM_RESULTS,
  MAX_EXAM_ATTEMPTS,
  type StudentStatus,
} from "@mizpah/shared";
import { api, AdminError } from "../adminClient.js";
import { badgeClass } from "../badge.js";
import QuizStats from "../../components/QuizStats.js";

interface ExamAttempt { attemptNumber: number; result: string; date: string; }
interface PaymentLog { at: string; by?: string; status: string; paidNow?: number; amountPaid?: number; amountDue?: number; note?: string; }
interface Student {
  studentId: string; prospectId?: string; batchId: string; courseId: string;
  fullName: string; email: string; phone?: string; mailingAddress?: string;
  feeUsd?: number;
  status: StudentStatus; enrolledAt: string;
  idph?: { status: string; attempts?: ExamAttempt[]; notes?: string };
  payment: { status: string; amountDue?: number; amountPaid?: number; notes?: string; updatedAt?: string; history?: PaymentLog[] };
}

const usd = (n?: number) => (n == null ? "-" : `$${n.toLocaleString()}`);

const todayISO = () => new Date().toISOString().slice(0, 10);

interface BatchOption { batchId: string; courseId: string; name: string; capacity: number; enrolledCount: number; }
interface HomeworkWeek { weekKey: string; status: string; files: { name: string; s3Key: string }[]; reviewed?: boolean; reviewedBy?: string; reviewedAt?: string; note?: string; }
interface QuizResult { quizId: string; windowDate: string; status: string; score: number | null; total: number | null; autoGraded: boolean; }

/** "56 / 77" for a graded attempt; falls back to the bare score if the set's
 * question count is unavailable, and "-" when there's nothing to show. */
const quizScoreText = (q: Pick<QuizResult, "status" | "score" | "total">): string => {
  if (q.status !== "submitted" || q.score == null) return "-";
  return q.total ? `${q.score} / ${q.total}` : String(q.score);
};
interface QuizBreak { questionId: string; chosen: string | null; correct: string; isRight: boolean; }
interface QuizDetail {
  quizId: string; windowDate: string; status: string; score: number | null; total: number;
  answeredCount?: number;
  answers?: Record<string, string>;
  questions: { questionId: string; prompt: string; choices: { key: string; text: string }[] }[];
  breakdown: QuizBreak[];
}

const quizSetLabel = (q: string) => { const m = /^set-(\d+)$/.exec(q); return m ? `Set ${m[1]}` : q; };
const quizDate = (d: string) => { const t = new Date(`${d}T00:00:00`); return Number.isNaN(t.getTime()) ? d : t.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); };

export default function StudentDetailPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [student, setStudent] = useState<Student | null>(null);
  const [batchName, setBatchName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [saved, setSaved] = useState("");
  const [reEnrollOpen, setReEnrollOpen] = useState(false);
  const [batches, setBatches] = useState<BatchOption[] | null>(null);
  const [reBatchId, setReBatchId] = useState("");
  const [quizzes, setQuizzes] = useState<QuizResult[]>([]);
  const [openQuiz, setOpenQuiz] = useState<QuizDetail | null>(null);
  const [loadingQuiz, setLoadingQuiz] = useState("");
  const [homework, setHomework] = useState<HomeworkWeek[] | null>(null);
  const [noteDraft, setNoteDraft] = useState<Record<string, string>>({});
  const [reviewBusy, setReviewBusy] = useState("");

  function openQuizDetail(quizId: string, windowDate: string) {
    setLoadingQuiz(`${quizId}#${windowDate}`); setOpenQuiz(null); setError("");
    api<QuizDetail>(`/admin/students/${id}/quizzes/${quizId}?windowDate=${encodeURIComponent(windowDate)}`)
      .then(setOpenQuiz)
      .catch((e) => setError(e instanceof AdminError ? e.message : "Failed to load quiz answers"))
      .finally(() => setLoadingQuiz(""));
  }

  const load = useCallback(() => {
    api<{ student: Student; batchName?: string }>(`/admin/students/${id}`)
      .then((d) => { setStudent(d.student); setBatchName(d.batchName ?? ""); })
      .catch((e) => setError(e instanceof AdminError ? e.message : "Failed to load student"));
    api<{ items: QuizResult[] }>(`/admin/students/${id}/quizzes`)
      .then((d) => setQuizzes(d.items))
      .catch(() => {});
  }, [id]);
  useEffect(load, [load]);

  // Homework status for this student (CNA only) - reuses the per-batch endpoint,
  // filtered to this student. Runs once the student (and its batchId) is loaded.
  const loadHomework = useCallback((batchId: string) => {
    api<{ rows: { studentId: string; weeks: HomeworkWeek[] }[] }>(`/admin/batches/${batchId}/homework`)
      .then((d) => setHomework(d.rows.find((r) => r.studentId === id)?.weeks ?? []))
      .catch(() => {});
  }, [id]);
  useEffect(() => {
    if (!student || !hasFullLifecycle(student.courseId)) return;
    loadHomework(student.batchId);
  }, [student, loadHomework]);

  // Acknowledge (or undo) a submitted week. One status per week.
  async function reviewWeek(weekKey: string, reviewed: boolean) {
    if (!student) return;
    setReviewBusy(weekKey); setError("");
    try {
      await api(`/admin/students/${id}/homework/${weekKey}/review`, {
        method: "PUT",
        body: { reviewed, note: reviewed ? (noteDraft[weekKey] ?? "").trim() || undefined : undefined },
      });
      loadHomework(student.batchId);
    } catch (e) { setError(e instanceof AdminError ? e.message : "Review failed"); }
    finally { setReviewBusy(""); }
  }

  function reviewControls(w: HomeworkWeek) {
    if (w.files.length === 0) return <span className="sub">-</span>;
    const busyHere = reviewBusy === w.weekKey;
    if (w.reviewed) {
      return (
        <div className="hw-review">
          <div className="hw-rev-by">
            Reviewed{w.reviewedBy ? ` by ${w.reviewedBy}` : ""}{w.reviewedAt ? ` · ${quizDate(w.reviewedAt.slice(0, 10))}` : ""}
          </div>
          {w.note && <div className="hw-rev-note">“{w.note}”</div>}
          <button className="rbtn" disabled={busyHere} onClick={() => reviewWeek(w.weekKey, false)}>
            {busyHere ? "…" : "Undo"}
          </button>
        </div>
      );
    }
    return (
      <div className="hw-review">
        <input
          className="ainput hw-note-input"
          placeholder="Optional note to student"
          maxLength={500}
          value={noteDraft[w.weekKey] ?? ""}
          onChange={(e) => setNoteDraft((d) => ({ ...d, [w.weekKey]: e.target.value }))}
        />
        <button className="rbtn coral" disabled={busyHere} onClick={() => reviewWeek(w.weekKey, true)}>
          {busyHere ? "Saving…" : "Mark reviewed"}
        </button>
      </div>
    );
  }

  async function dlHomework(key: string) {
    try {
      const { url } = await api<{ url: string }>(`/admin/homework/download-url?key=${encodeURIComponent(key)}`);
      window.open(url, "_blank");
    } catch (e) { setError(e instanceof AdminError ? e.message : "Download failed"); }
  }

  async function act(label: string, fn: () => Promise<unknown>) {
    setBusy(label); setError(""); setSaved("");
    try {
      await fn(); load();
      setSaved(label); window.setTimeout(() => setSaved((s) => (s === label ? "" : s)), 2500);
    }
    catch (e) { setError(e instanceof AdminError ? e.message : "Action failed"); }
    finally { setBusy(""); }
  }

  function openReEnroll(courseId: string) {
    setReEnrollOpen(true); setError("");
    if (batches) return;
    api<{ items: BatchOption[] }>("/admin/batches")
      .then((d) => {
        const forCourse = d.items.filter((b) => b.courseId === courseId);
        setBatches(forCourse);
        const open = forCourse.find((b) => b.enrolledCount < b.capacity);
        if (open) setReBatchId(open.batchId);
      })
      .catch((e) => setError(e instanceof AdminError ? e.message : "Failed to load batches"));
  }

  async function reEnroll(prospectId: string) {
    if (!reBatchId) return;
    setBusy("reenroll"); setError("");
    try {
      const res = await api<{ student: { studentId: string } }>(`/admin/inquiries/${prospectId}/enroll`, { method: "POST", body: { batchId: reBatchId } });
      nav(`/admin/students/${res.student.studentId}`);
    } catch (e) {
      if (e instanceof AdminError && e.status === 409 && e.fields?.studentId) {
        nav(`/admin/students/${e.fields.studentId}`);
        return;
      }
      setError(e instanceof AdminError ? e.message : "Re-enrollment failed");
    } finally {
      setBusy("");
    }
  }

  if (error && !student) return <div className="login-err">{error}</div>;
  if (!student) return <p className="subhead">Loading...</p>;

  const idphApplicable = !!courseById(student.courseId)?.idphRequired;
  const quizApplicable = hasFullLifecycle(student.courseId); // quizzes are CNA-only
  const canReEnroll = student.status === "Withdrawn" && !!student.prospectId;

  return (
    <>
      <p className="subhead"><Link to="/admin/batches">← Batches</Link></p>
      {error && <div className="login-err" style={{ marginBottom: 12 }}>{error}</div>}
      <div className="detail">
        <div className="card pad">
          <div className="row">
            <h1 style={{ fontSize: 22 }}>{student.fullName}</h1>
            <span className={badgeClass(student.status)}>{student.status}</span>
          </div>
          <dl className="kv">
            <dt>Email</dt><dd>{student.email}</dd>
            <dt>Phone</dt><dd>{student.phone || "-"}</dd>
            <dt>Mailing address</dt><dd>{student.mailingAddress || "-"}</dd>
            <dt>Program</dt><dd>{courseById(student.courseId)?.name ?? student.courseId}</dd>
            <dt>Batch</dt><dd><Link to={`/admin/batches/${student.batchId}`}>{batchName || "View batch"}</Link></dd>
            <dt>Enrolled</dt><dd>{new Date(student.enrolledAt).toLocaleDateString()}</dd>
            {student.prospectId && <><dt>Lead</dt><dd><Link to={`/admin/inquiries/${student.prospectId}`}>Original inquiry</Link></dd></>}
          </dl>
          <div className="alabel">Lifecycle status<SaveCue busy={busy} saved={saved} label="status" /></div>
          <select className="ainput" aria-label="Lifecycle status" value={student.status} disabled={!!busy}
            onChange={(e) => act("status", () => api(`/admin/students/${id}/status`, { method: "PATCH", body: { status: e.target.value } }))}>
            {STUDENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>

          {canReEnroll && (
            <div style={{ marginTop: 16 }}>
              {!reEnrollOpen ? (
                <button className="rbtn coral" disabled={!!busy} onClick={() => openReEnroll(student.courseId)}>Re-enroll into a new batch</button>
              ) : (
                <>
                  <div className="alabel">Re-enroll into a new batch</div>
                  {batches === null ? (
                    <p className="sub">Loading batches...</p>
                  ) : batches.length === 0 ? (
                    <p className="sub">No {courseById(student.courseId)?.name ?? "matching"} batches yet. Create one on the Batches screen.</p>
                  ) : (
                    <>
                      <select className="ainput" aria-label="Batch to re-enroll into" value={reBatchId} onChange={(e) => setReBatchId(e.target.value)}>
                        {batches.map((b) => {
                          const remaining = b.capacity - b.enrolledCount;
                          return <option key={b.batchId} value={b.batchId} disabled={remaining <= 0}>{b.name} ({remaining > 0 ? `${remaining} seats left` : "full"})</option>;
                        })}
                      </select>
                      <div className="actions" style={{ marginTop: 12 }}>
                        <button className="rbtn coral" disabled={!!busy || !reBatchId} onClick={() => reEnroll(student.prospectId!)}>{busy === "reenroll" ? "Re-enrolling..." : "Confirm re-enrollment"}</button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="card pad">
          <PaymentSection key={`pay-${student.payment?.updatedAt ?? "init"}`} student={student} busy={busy} saved={saved} act={act} id={id} />
          {idphApplicable && <IdphSection student={student} busy={busy} saved={saved} act={act} id={id} />}
        </div>
      </div>

      {quizApplicable && homework && (
        <div className="card pad" style={{ marginTop: 16 }}>
          <div className="alabel" style={{ marginTop: 0 }}>Homework</div>
          {homework.length === 0 ? (
            <p className="sub">No homework weeks yet.</p>
          ) : (
            <>
              <table>
                <thead><tr><th>Week</th><th>Status</th><th>Submission</th><th>Review</th></tr></thead>
                <tbody>
                  {homework.map((w) => (
                    <tr key={w.weekKey}>
                      <td><div className="nm">Week {w.weekKey.slice(2)}</div></td>
                      <td><span className={badgeClass(w.status)}>{w.status}</span></td>
                      <td>
                        {w.files.length === 0
                          ? <span className="sub">-</span>
                          : w.files.map((f) => (
                            <div key={f.s3Key} className="sub">
                              <a href="#" onClick={(e) => { e.preventDefault(); dlHomework(f.s3Key); }}>{f.name}</a>
                            </div>
                          ))}
                      </td>
                      <td>{reviewControls(w)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="cards-list">
                {homework.map((w) => (
                  <div key={w.weekKey} className="card pad" style={{ marginBottom: 10 }}>
                    <div className="row"><div className="nm">Week {w.weekKey.slice(2)}</div><span className={badgeClass(w.status)}>{w.status}</span></div>
                    {w.files.length === 0
                      ? <div className="sub" style={{ marginTop: 6 }}>No submission</div>
                      : <div className="sub" style={{ marginTop: 6 }}>{w.files.map((f) => (
                          <div key={f.s3Key}><a href="#" onClick={(e) => { e.preventDefault(); dlHomework(f.s3Key); }}>{f.name}</a></div>
                        ))}</div>}
                    {w.files.length > 0 && <div style={{ marginTop: 8 }}>{reviewControls(w)}</div>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {quizApplicable && (
      <div className="card pad" style={{ marginTop: 16 }}>
        <div className="alabel" style={{ marginTop: 0 }}>Quiz results</div>
        {quizzes.length === 0 ? (
          <p className="sub">No quizzes taken yet.</p>
        ) : (
          <>
            <p className="sub" style={{ marginTop: 0 }}>Click a quiz to see the student's answers.</p>
            <table>
              <thead><tr><th>Quiz</th><th>Date</th><th>Score</th><th>Status</th></tr></thead>
              <tbody>
                {quizzes.map((q) => (
                  <tr key={`${q.quizId}#${q.windowDate}`} onClick={() => openQuizDetail(q.quizId, q.windowDate)} style={{ cursor: "pointer" }}>
                    <td><div className="nm">{quizSetLabel(q.quizId)}</div></td>
                    <td className="sub">{quizDate(q.windowDate)}</td>
                    <td>{quizScoreText(q)}</td>
                    <td>
                      <span className={badgeClass(q.status === "submitted" ? "Passed" : "Pending")}>{q.status === "submitted" ? "Submitted" : "In progress"}</span>
                      {q.status === "submitted" && q.autoGraded && <span className="sub"> · auto</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="cards-list">
              {quizzes.map((q) => (
                <div key={`${q.quizId}#${q.windowDate}`} className="card pad" style={{ marginBottom: 10, cursor: "pointer" }} onClick={() => openQuizDetail(q.quizId, q.windowDate)}>
                  <div className="row"><div className="nm">{quizSetLabel(q.quizId)}</div><span className={badgeClass(q.status === "submitted" ? "Passed" : "Pending")}>{q.status === "submitted" ? "Submitted" : "In progress"}</span></div>
                  <div className="sub" style={{ marginTop: 4 }}>{quizDate(q.windowDate)} · Score: {quizScoreText(q)}</div>
                </div>
              ))}
            </div>
            {loadingQuiz && <p className="sub" style={{ marginTop: 10 }}>Loading answers...</p>}
          </>
        )}
      </div>
      )}

      {openQuiz && (() => {
        const submitted = openQuiz.status === "submitted";
        const bd = new Map(openQuiz.breakdown.map((b) => [b.questionId, b]));
        const answers = openQuiz.answers ?? {};
        return (
        <div className="card pad" style={{ marginTop: 16 }}>
          <div className="row">
            <div className="alabel" style={{ marginTop: 0 }}>
              {quizSetLabel(openQuiz.quizId)} · {quizDate(openQuiz.windowDate)} · {submitted ? `Score ${openQuiz.score}/${openQuiz.total}` : "In progress"}
            </div>
            <button className="rbtn" onClick={() => setOpenQuiz(null)}>Close</button>
          </div>
          {submitted
            ? <QuizStats breakdown={openQuiz.breakdown} score={openQuiz.score ?? 0} total={openQuiz.total} />
            : <p className="sub" style={{ marginTop: 0 }}>Not submitted yet - answered {openQuiz.answeredCount ?? 0} of {openQuiz.total}. No score until the student submits.</p>}
          {openQuiz.questions.map((qq, i) => {
            const b = bd.get(qq.questionId);
            const chosenKey = submitted ? (b?.chosen ?? null) : (answers[qq.questionId] || null);
            return (
              <div key={qq.questionId} className="quiz-q" style={{ marginTop: 12 }}>
                <div className="qprompt">
                  {i + 1}. {qq.prompt}{" "}
                  {submitted
                    ? (!b?.chosen
                        ? <span className="qmark none" title="Not answered">-</span>
                        : b.isRight
                          ? <span className="qmark ok" title="Correct">✓</span>
                          : <span className="qmark bad" title="Incorrect">✗</span>)
                    : (chosenKey
                        ? <span className="qmark none" title="Answered">●</span>
                        : <span className="qmark none" title="Not answered">-</span>)}
                </div>
                {qq.choices.map((c) => {
                  const chosen = chosenKey === c.key;
                  const correct = submitted && b?.correct === c.key;
                  const cls = correct ? " correct" : chosen ? " chosenwrong" : "";
                  return (
                    <div key={c.key} className={`quiz-opt${cls}`}>
                      <span>{c.key}. {c.text}</span>
                      {correct && <span className="tag ok">correct</span>}
                      {chosen && !correct && <span className="tag bad">{submitted ? "student's answer" : "selected"}</span>}
                    </div>
                  );
                })}
                {!chosenKey && <div className="sub" style={{ marginTop: 4 }}>No answer given.</div>}
              </div>
            );
          })}
          <div className="actions" style={{ marginTop: 16 }}>
            <button className="rbtn" onClick={() => { setOpenQuiz(null); window.scrollTo({ top: 0 }); }}>Close</button>
          </div>
        </div>
        );
      })()}
    </>
  );
}

/** Transient inline feedback for auto-saving controls (no explicit save button). */
function SaveCue({ busy, saved, label }: { busy: string; saved: string; label: string }) {
  if (busy === label) return <span className="sub"> · Saving...</span>;
  if (saved === label) return <span className="sub savedcue"> · Saved ✓</span>;
  return null;
}

type SectionProps = {
  student: Student; busy: string; saved: string; id: string;
  act: (label: string, fn: () => Promise<unknown>) => Promise<void>;
};

function PaymentSection({ student, busy, saved, act, id }: SectionProps) {
  const p = student.payment ?? { status: "Pending" };
  const fee = student.feeUsd; // course fee snapshotted at enrollment (may be undefined)
  // Remaining balance carried forward from the saved record (full fee until any
  // payment is recorded). This component is remounted (keyed on payment.updatedAt)
  // after each save, so these initializers re-read the freshly-saved state.
  const prevDue = p.amountDue ?? fee;
  const [amountDue, setAmountDue] = useState(prevDue != null ? String(prevDue) : "");
  // The "payment received this time" box always starts empty - it's a single
  // installment, recorded in the activity log and added to the running total.
  const [payAmount, setPayAmount] = useState("");
  const [notes, setNotes] = useState("");

  // Entering this transaction's payment decrements the remaining balance
  // (previous due - paid), clamped at 0. The due field stays editable.
  function onPayChange(v: string) {
    setPayAmount(v);
    if (prevDue != null) {
      const pay = Number(v) || 0;
      setAmountDue(String(Math.max(0, prevDue - pay)));
    }
  }

  // Live preview of the auto-derived status for the values being entered.
  const receivedAfter = (p.amountPaid ?? 0) + (Number(payAmount) || 0);
  const dueAfter = amountDue === "" ? undefined : Number(amountDue);
  const willBe = derivePaymentStatus(receivedAfter, dueAfter);

  function save() {
    act("payment", () => api(`/admin/students/${id}/payment`, {
      method: "PUT",
      body: {
        amountDue: amountDue === "" ? undefined : Number(amountDue),
        paymentAmount: payAmount === "" ? undefined : Number(payAmount),
        notes: notes || undefined,
      },
    }));
  }

  // Prominent figures come from the SAVED record (refreshed after each save),
  // not the editable fields - "always show how much we got and how much is due".
  const paid = p.amountPaid;
  const due = p.amountDue ?? (fee != null ? Math.max(0, fee - (paid ?? 0)) : undefined);
  const settled = due === 0;
  const log = [...(p.history ?? [])].reverse(); // newest first

  return (
    <section>
      <div className="row" style={{ marginBottom: 10 }}>
        <div className="alabel" style={{ marginTop: 0 }}>Payment</div>
        <span className={badgeClass(p.status)}>{p.status}</span>
      </div>

      <div className="paystats">
        <div className="paystat paid">
          <div className="lbl">Received</div>
          <div className="val">{usd(paid ?? 0)}</div>
        </div>
        <div className={`paystat due${settled ? " clear" : ""}`}>
          <div className="lbl">Balance due</div>
          <div className="val">{settled ? "Paid in full" : usd(due)}</div>
        </div>
      </div>

      <p className="sub" style={{ marginTop: 0, marginBottom: 10 }}>
        {fee != null ? <>Course fee: <strong>${fee.toLocaleString()}</strong>. </> : "No course fee on file. "}
        Enter each payment as it's received - it's added to the total and logged below.
      </p>
      <div className="g2form">
        <label className="fld"><span className="alabel">Payment received</span>
          <input className="ainput" type="number" min={0} value={payAmount} onChange={(e) => onPayChange(e.target.value)} placeholder="0" />
        </label>
        <label className="fld"><span className="alabel">Balance due</span>
          <input className="ainput" type="number" min={0} value={amountDue} onChange={(e) => setAmountDue(e.target.value)} placeholder="0" />
        </label>
        <label className="fld"><span className="alabel">Note</span>
          <input className="ainput" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Method, e.g. cash, check #..." />
        </label>
      </div>
      <p className="sub" style={{ marginTop: 10, marginBottom: 0 }}>
        Status updates automatically → <span className={badgeClass(willBe)}>{willBe}</span>
      </p>
      <div className="actions" style={{ marginTop: 12 }}>
        <button className="rbtn coral" disabled={!!busy} onClick={save}>{busy === "payment" ? "Saving..." : "Save payment"}</button>
        {saved === "payment" && <span className="sub savedcue" style={{ alignSelf: "center" }}>Saved ✓</span>}
      </div>

      <div className="alabel">Activity log</div>
      <ul className="tl">
        {log.length === 0 && <li>No payment activity yet.</li>}
        {log.map((h, i) => (
          <li key={i}>
            <strong>{h.status}</strong>
            {h.paidNow != null
              ? <> - Received {usd(h.paidNow)}{h.amountDue != null ? <> · Balance {usd(h.amountDue)}</> : null}</>
              : h.amountDue != null ? <> - Balance {usd(h.amountDue)}</> : null}
            {h.note ? ` · ${h.note}` : ""}
            <div className="t">{new Date(h.at).toLocaleString()}{h.by ? ` · ${h.by}` : ""}</div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function IdphSection({ student, busy, saved, act, id }: SectionProps) {
  const idph = student.idph ?? { status: "Draft", attempts: [] };
  const attempts = idph.attempts ?? [];
  const [result, setResult] = useState(EXAM_RESULTS[0] as string);
  const [date, setDate] = useState(todayISO());

  const canRecord = idph.status === "Pending Exam" && attempts.length < MAX_EXAM_ATTEMPTS;

  return (
    <section style={{ marginTop: 22, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
      <div className="row" style={{ marginBottom: 8 }}>
        <div className="alabel" style={{ marginTop: 0 }}>IDPH</div>
        <span className={badgeClass(idph.status)}>{idph.status}</span>
      </div>
      <div className="alabel">Authorization status<SaveCue busy={busy} saved={saved} label="idph" /></div>
      <select className="ainput" aria-label="IDPH authorization status" value={idph.status} disabled={!!busy}
        onChange={(e) => act("idph", () => api(`/admin/students/${id}/idph`, { method: "PUT", body: { status: e.target.value } }))}>
        {IDPH_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>

      <div className="alabel">Exam attempts ({attempts.length}/{MAX_EXAM_ATTEMPTS})</div>
      <ul className="tl">
        {attempts.length === 0 && <li>No attempts recorded.</li>}
        {attempts.map((a) => (
          <li key={a.attemptNumber}>
            <strong>Attempt {a.attemptNumber}: {a.result}</strong>
            <div className="t">{a.date}</div>
          </li>
        ))}
      </ul>

      {canRecord ? (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select className="ainput" aria-label="Exam result" style={{ flex: "0 0 120px" }} value={result} onChange={(e) => setResult(e.target.value)}>
              {EXAM_RESULTS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input className="ainput" aria-label="Exam date" style={{ flex: 1, minWidth: 140 }} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="actions" style={{ marginTop: 12 }}>
            <button className="rbtn" disabled={!!busy} onClick={() => act("attempt", () => api(`/admin/students/${id}/exam-attempt`, { method: "POST", body: { result, date } }))}>
              {busy === "attempt" ? "Recording..." : "Record attempt"}
            </button>
            {saved === "attempt" && <span className="sub savedcue" style={{ alignSelf: "center" }}>Saved ✓</span>}
          </div>
        </>
      ) : (
        <p className="sub">
          {idph.status !== "Pending Exam"
            ? 'Set status to "Pending Exam" to record an exam attempt.'
            : "All 3 attempts used."}
        </p>
      )}
    </section>
  );
}
